import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { getEntityTypeOptions } from "../RiskScenarios.hooks";
import { allOptionId, resolveSelectionWithAll } from "../../common/RiskSettings.utils";
import { IRiskScenarioDialog } from "./RiskScenarioDialog";
import { RiskScenarioCategory, RiskScenarioSaveModel } from "./RiskScenarioDialog.types";

const emptyModel: RiskScenarioSaveModel = { id: "", name: "", description: "", category: "", riskAreaIds: [], entityTypes: [] };

function initialSelection(values: string[] | undefined) {
    return values && values.length > 0 ? values : [allOptionId];
}

export function useRiskScenarioDialogDataMapping(props: IRiskScenarioDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskScenarioCategory, translateEntityType } = useTranslation();
    const { isLoading: riskAreasLoading, riskAreas } = useRiskAreas();

    const [viewModel, setViewModel] = useState<RiskScenarioSaveModel>(props.selectedRiskScenario ?? emptyModel);
    const [chosenRiskAreas, setChosenRiskAreas] = useState<string[]>(() => initialSelection(props.selectedRiskScenario?.riskAreaIds));
    const [chosenEntityTypes, setChosenEntityTypes] = useState<string[]>(() => initialSelection(props.selectedRiskScenario?.entityTypes.map(String)));

    const validation = useValidator<{ name: string; category: string; riskAreas: string[]; entityTypes: string[] }>((validators) => ({
        name: validators.validateNotEmpty((x) => x.name, translateString("riskScenario")),
        category: validators.validateNotEmpty((x) => x.category, translateString("category")),
        riskAreas: validators.validateNotEmpty((x) => x.riskAreas, translateString("riskAreas")),
        entityTypes: validators.validateNotEmpty((x) => x.entityTypes, translateString("types")),
    }));

    validation.check({ name: viewModel.name, category: viewModel.category, riskAreas: chosenRiskAreas, entityTypes: chosenEntityTypes });

    const categoryOptions = Object.values(RiskScenarioCategory)
        .filter((value): value is RiskScenarioCategory => typeof value === "number")
        .map((category) => ({ id: category.toString(), name: translateRiskScenarioCategory(category) }));

    const riskAreaOptions = [{ id: allOptionId, name: translateString("allRiskAreas") }, ...(riskAreas ?? [])];

    const entityTypeOptions = [{ id: allOptionId, name: translateString("allTypes") }, ...getEntityTypeOptions(translateEntityType)];

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onSave();
                props.onCloseDialog();
                snackbar.show(createElement(Trans, { i18nKey: isEdit() ? "dataUpdated" : "dataCreated", values: { name: viewModel.name } }));
            }
        },
    });

    function save() {
        const body = {
            name: viewModel.name,
            description: viewModel.description,
            category: Number(viewModel.category),
            riskAreaIds: viewModel.riskAreaIds,
            entityTypes: viewModel.entityTypes,
        };
        return isEdit() ? put<string>(`/risk-scenarios/${viewModel.id}`, body) : post<string>("/risk-scenarios", body);
    }

    const setName = (name: string) => {
        setViewModel({ ...viewModel, name });
    };

    const setDescription = (description: string) => {
        setViewModel({ ...viewModel, description });
    };

    const setCategory = (category: string | null) => {
        setViewModel({ ...viewModel, category: category ?? "" });
    };

    const setRiskAreas = (values: string[]) => {
        const selection = resolveSelectionWithAll(values, chosenRiskAreas);
        setChosenRiskAreas(selection);
        setViewModel({ ...viewModel, riskAreaIds: selection.includes(allOptionId) ? [] : selection });
    };

    const setEntityTypes = (values: string[]) => {
        const selection = resolveSelectionWithAll(values, chosenEntityTypes);
        setChosenEntityTypes(selection);
        setViewModel({ ...viewModel, entityTypes: selection.includes(allOptionId) ? [] : selection.map(Number) });
    };

    const validateAndSave = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        saveMutation.mutateAsync();
    };

    function isEdit() {
        return !isNullOrWhitespace(viewModel.id);
    }

    return {
        viewModel,
        validation,
        inProgress: saveMutation.isLoading,
        isEdit,
        setName,
        setDescription,
        setCategory,
        setRiskAreas,
        chosenRiskAreas,
        setEntityTypes,
        chosenEntityTypes,
        entityTypeOptions,
        validateAndSave,
        categoryOptions,
        riskAreasLoading,
        riskAreaOptions,
    };
}
